import { Logger } from '@saviynt/common';

/**
 * Form Layout Configuration
 * Controls the maximum number of columns per row in form layouts
 *
 * To change MAX_COLUMNS, update the value below. All components will adapt automatically.
 */
export const FORM_LAYOUT_CONFIG = {
  /**
   * Maximum columns per row
   * Can be set to 1, 2, 3, 4, 5, or any positive integer
   * Default: 4 (maintains current behavior)
   *
   * Special cases:
   * - MAX_COLUMNS = 1: All fields full-width, no multi-column layouts
   * - MAX_COLUMNS = 2: Only 1 or 2 column layouts possible
   * - MAX_COLUMNS >= 3: Full multi-column layout support
   */
  MAX_COLUMNS: 4 as number,

  /**
   * Minimum columns (always 1)
   */
  MIN_COLUMNS: 1,

  /**
   * Default colspan for new fields
   * Set to half of MAX_COLUMNS for balanced layouts
   * Special cases:
   * - MAX_COLUMNS = 1: DEFAULT_COLSPAN = 1 (full width, only option)
   * - MAX_COLUMNS = 2: DEFAULT_COLSPAN = 1 (half width)
   * - MAX_COLUMNS >= 3: DEFAULT_COLSPAN = floor(MAX_COLUMNS / 2)
   */
  get DEFAULT_COLSPAN(): number {
    if (this.MAX_COLUMNS === 1) {
      return 1; // Only option for single column
    }

    return Math.max(1, Math.floor(this.MAX_COLUMNS / 2));
  },

  /**
   * Minimum recommended MAX_COLUMNS value
   * Values below this may have limited functionality
   */
  MIN_RECOMMENDED_COLUMNS: 2,

  /**
   * Maximum recommended MAX_COLUMNS value
   * Values above this may require additional CSS classes
   */
  MAX_RECOMMENDED_COLUMNS: 6,
};

/**
 * Get maximum columns configuration
 * Single source of truth for all components
 */
export const getMaxColumns = (): number => FORM_LAYOUT_CONFIG.MAX_COLUMNS;

/**
 * Validate colspan value against max columns
 */
export const validateColSpan = (colSpan: number): boolean => {
  const max = getMaxColumns();

  return colSpan >= FORM_LAYOUT_CONFIG.MIN_COLUMNS && colSpan <= max;
};

/**
 * Clamp colspan to valid range
 */
export const clampColSpan = (colSpan: number): number => {
  const max = getMaxColumns();

  return Math.max(FORM_LAYOUT_CONFIG.MIN_COLUMNS, Math.min(max, colSpan));
};

/**
 * Clamp colspan for rendering (preserves legacy values up to 4)
 * Use this when rendering existing forms to support legacy colSpan: 4
 */
export const clampColSpanForRendering = (colSpan: number): number => {
  const max = getMaxColumns();
  const legacyMax = Math.max(max, 4); // Support legacy colSpan: 4

  return Math.max(FORM_LAYOUT_CONFIG.MIN_COLUMNS, Math.min(legacyMax, colSpan));
};

/**
 * Check if colSpan is valid for rendering (includes legacy support)
 * Returns true if colSpan can be rendered (even if > MAX_COLUMNS but <= 4)
 */
export const isValidColSpanForRendering = (colSpan: number): boolean => {
  const max = getMaxColumns();
  const legacyMax = Math.max(max, 4); // Support legacy colSpan: 4

  return colSpan >= FORM_LAYOUT_CONFIG.MIN_COLUMNS && colSpan <= legacyMax;
};

/**
 * Generate array of valid colspan values
 */
export const getValidColSpanValues = (): number[] => {
  const max = getMaxColumns();

  if (max < 1) {
    return [1]; // Fallback to minimum
  }

  return Array.from({ length: max }, (_, i) => i + 1);
};

/**
 * Validate MAX_COLUMNS configuration
 * Returns warnings if MAX_COLUMNS is outside recommended range
 */
export const validateMaxColumnsConfig = (): {
  isValid: boolean;
  warnings: string[];
} => {
  const max = getMaxColumns();
  const warnings: string[] = [];

  if (max < FORM_LAYOUT_CONFIG.MIN_RECOMMENDED_COLUMNS) {
    warnings.push(
      `MAX_COLUMNS (${max}) is below recommended minimum (${FORM_LAYOUT_CONFIG.MIN_RECOMMENDED_COLUMNS}). ` +
        `Multi-column layouts will be limited.`
    );
  }

  if (max === 1) {
    warnings.push(
      'MAX_COLUMNS = 1 means all fields will be full-width. ' +
        'No multi-column layouts will be possible.'
    );
  }

  if (max > FORM_LAYOUT_CONFIG.MAX_RECOMMENDED_COLUMNS) {
    warnings.push(
      `MAX_COLUMNS (${max}) exceeds recommended maximum (${FORM_LAYOUT_CONFIG.MAX_RECOMMENDED_COLUMNS}). ` +
        `Additional CSS classes may need to be added manually.`
    );
  }

  return {
    isValid: max >= 1,
    warnings,
  };
};

// Validate configuration on module load
const validateConfiguration = () => {
  const maxColumns = FORM_LAYOUT_CONFIG.MAX_COLUMNS;

  if (maxColumns < 1) {
    Logger.error('MAX_COLUMNS must be at least 1. Using fallback value of 4.');
    // Note: Can't modify const, but will log error
  }

  if (maxColumns > 10) {
    Logger.warn(
      `MAX_COLUMNS (${maxColumns}) is very high. ` +
        `Consider if this is intentional. Performance may be affected.`
    );
  }

  const validation = validateMaxColumnsConfig();

  if (validation.warnings.length > 0) {
    Logger.warn('MAX_COLUMNS configuration warnings:', validation.warnings);
  }
};

// Run validation on module load (browser only)
if (typeof window !== 'undefined') {
  validateConfiguration();
}
